'use strict'
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import crypto from 'crypto';
import SessionContext from '../contexts/SessionContext';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { getFirebaseApp } from '../config/firebase';

const initialState = {
  signedIn: false,
  cart: { items: [] },
};

export default function SessionProvider({ url: initialUrl, children, user, config = {}, store: storeSlug }) {
  const [session, setSession] = useState({ ...initialState, ...config });
  const [loadingSession, setLoadingSession] = useState(true);
  const [address, setAddress] = useState({});
  const API_URL = config?.payfac_api_url ?? null;
  const EXTERNAL_ACC_ID = config?.payfac_account_id ?? null;

  let url = initialUrl;
  if (storeSlug) {
    url += url.includes('?') ? '&' : '?';
    url += qs.stringify({ storeSlug });
  }

  const getUserToken = async () => {
    if (user && typeof user.getIdToken === 'function') return user.getIdToken();
  };

  const reqHeader = async () => {
    const token = await getUserToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const formatData = async (other, paymentMethod, address, order_data, paymentRes, apiCall = 'intent') => {
    const fullName = address?.holderName || '';
    const [firstName, ...rest] = fullName.split(' ');
    const lastName = rest.join(' ');

    let data = { ...other };
    if (apiCall !== 'intent') {
      const {
        encryptedCardNumber,
        encryptedExpiryMonth,
        encryptedExpiryYear,
        encryptedSecurityCode,
      } = paymentMethod || {};
      data = {
        card_number: encryptedCardNumber,
        card_month_exp: encryptedExpiryMonth,
        card_year_exp: encryptedExpiryYear,
        card_cvv: encryptedSecurityCode,
        card_name: firstName || '',
        external_account_id: EXTERNAL_ACC_ID,
        payment_id: paymentRes?.result?.payment_id || null,
      };
      data.neworder = paymentRes.orderResponse?.neworder;
    }

    data.shopper_details = {
      device_id: '',
      device_type: '',
      browser_info: {
        userAgent: window.navigator.userAgent || null,
        language: window.navigator.language || null,
        colorDepth: window.screen.colorDepth,
        javaEnabled: window.navigator.javaEnabled(),
        javaScriptEnabled: true,
        screenWidth: window.screen.width,
        timeZoneOffset: new Date().getTimezoneOffset(),
      },
    }
    data.billing_address = {
      zip: address?.zipcode || '',
      first_name: firstName || '',
      last_name: lastName || '',
      address_1: [address?.unit, address?.street].filter(Boolean).join(', ') || '',
      address_2: '',
      city: address?.cityName || '',
      two_letter_state: address?.short_statename || '',
      two_letter_country: 'US',
      phone: ''
    };
    data.shipping_address = {
      zip: address?.shipping_zip || '',
      address_1: address?.shipping_address || '',
      address_2: '',
      city: address?.shipping_city || '',
      two_letter_state: address?.shipping_state || '',
      two_letter_country: 'US',
      email: order_data?.address?.email || '',
      phone: order_data?.address?.mobile || '',
    };
    data.order_data = order_data;
    return data;
  };

  const context = useMemo(() => ({
    config,
    session,
    loadingSession,
    getUser: user,
    getUserToken,
    address,
    reqHeader,
    actions: {
      getJwtToken: async () => {
        const headers = await reqHeader();
        const res = await fetch(`/api/store/${storeSlug}/adyen/getJwtToken`, {
          method: 'POST',
          headers,
        });
        return res.json();
      },
      decodeKey: async (encryptedData) => {
        try {
          if (!encryptedData) return '';
          const [salt, iv, encrypted] = encryptedData.split(':');
          const pass = 'bottlezoo_keys_secrets';
          const key = crypto.pbkdf2Sync(pass, Buffer.from(salt, 'hex'), 10000, 32, 'sha512');
          const decipher = crypto.createDecipheriv('aes-256-cbc', key, Buffer.from(iv, 'hex'));
          let decrypted = decipher.update(encrypted, 'hex', 'utf8');
          decrypted += decipher.final('utf8');
          return decrypted;
        } catch {
          return '';
        }
      },

      saveAuthorizelogs: async (data) => {
        const headers = await reqHeader();
        const res = await fetch(`/api/store/${storeSlug}/addOnlinePaymentLogs`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ request: { payment_gateway: 'authorize' }, response: data }),
        });
        return res.json();
      },

      setAddress: (addr) => {
        if (addr?.geometry) {
          localStorage.setItem('userAddress', JSON.stringify(addr));
          setAddress(addr);
        }
      },

      createPaymentIntent: async (data) => {
        try {
          const MAX_RETRIES = 2;
          let retries = 0;
          let response = {};
          const params = typeof data === 'string' ? JSON.parse(data) : data;
          const { paymentMethod, billing_address, order_data, ...other } = params;
          const headers = await reqHeader();

          let lastFailedStep = null;
          let paymentExists = false;
          let orderResponse = {};
          let paymentIntentCreated = false;

          while (retries < MAX_RETRIES) {
            try {
              const api_params = await formatData(other, paymentMethod, billing_address, order_data, response, 'intent');
              order_data.order_id = other.external_payment_id;
              const intentpar = { ...api_params, order_data };

              if (!paymentExists && !paymentIntentCreated && (!lastFailedStep || lastFailedStep === 'createPaymentIntent')) {
                const res = await fetch(`/api/store/${storeSlug}/adyen/createPaymentIntent`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify(intentpar),
                });
                response = await res.json();

                if (response?.error?.includes("external_transaction_id that already exists")) {
                  const checkRes = await fetch(`/api/store/${storeSlug}/adyen/checkPaymentStatus`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      external_payment_id: other.external_payment_id,
                      external_account_id: EXTERNAL_ACC_ID,
                      type: 'by_external_id',
                      payment_id: null,
                    }),
                  });
                  orderResponse = response?.orderResponse || {};
                  const checkStatus = await checkRes.json();

                  if (['succeeded', 'capture_requested'].includes(checkStatus.status)) {
                    return { status: true, checkStatus };
                  } else if (checkStatus.status === 'requires_payment_method') {
                    paymentExists = true;
                    lastFailedStep = 'paymentauth';
                    response = { result: checkStatus, orderResponse };
                  } else {
                    return { error: checkStatus?.last_error || 'Unexpected error', status: false, response };
                  }
                } else if (response?.error) {
                  retries++;
                  lastFailedStep = 'createPaymentIntent';
                  if (retries >= MAX_RETRIES) return response;
                  continue;
                } else {
                  paymentIntentCreated = true;
                  orderResponse = response?.orderResponse || {};
                  lastFailedStep = null;
                }
              }
              if (lastFailedStep === 'paymentauth' || paymentExists || paymentIntentCreated) {
                let auth_params = await formatData(other.external_payment_id, paymentMethod, billing_address, order_data, response, 'authApi');
                const authRes = await fetch(`/api/store/${storeSlug}/adyen/paymentauth`, {
                  method: 'POST',
                  headers,
                  body: JSON.stringify(auth_params),
                });

                const authresp = await authRes.json();
                if (authresp?.error) {
                  retries++;
                  lastFailedStep = 'paymentauth';
                  if (retries >= MAX_RETRIES) return authresp;
                  continue;
                }

                if (['succeeded', 'capture_requested'].includes(authresp.status)) {
                  return { status: true, resStatus: authresp };
                }

                while (true) {
                  const check = await fetch(`/api/store/${storeSlug}/adyen/checkPaymentStatus`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                      payment_id: authresp.payment_id,
                      external_account_id: EXTERNAL_ACC_ID,
                      type: 'by_payment_id',
                      orderResponse,
                      order_data,
                      billing_address,
                      payment_method_id: authresp?.payment_method_id || null,
                    }),
                  });

                  const resStatus = await check.json();
                  if (['succeeded', 'capture_requested'].includes(resStatus.status)) {
                    return { status: true, resStatus };
                  }
                  if (resStatus.status === 'error') return { error: 'Something went wrong', status: false, resStatus };
                  if (resStatus.status === 'canceled') return { error: 'Payment canceled from server', status: false, resStatus };
                }
              }
            } catch (err) {
              retries++;
              if (retries >= MAX_RETRIES) return err?.message || 'Payment intent creation failed!';
            }
          }
        } catch (e) {
          return e?.message || 'Payment process failed!';
        }
      },
     
     /**
     * Signs an existing user in
     * @param {String} token The user's token
     */
    signIn: async function signIn({ token }) {
      setLoadingSession(true);
      const requrl = !storeSlug ? '/api/signIn' : `/api/store/${storeSlug}/signIn`;

      const response = await fetch(requrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      console.log("response:::",response,response.ok)
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'An error occurred during sign in');
      }

      setSession({ ...session, ...result });
      setLoadingSession(false);
    },

    /**
     * Signs the user out
     */
    signOut: async function signOut() {
      setLoadingSession(true);
      const requrl = !storeSlug ? '/api/signOut' : `/api/store/${storeSlug}/signOut`;

      const response = await fetch(requrl, {
        method: 'POST'
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'An error occurred during sign out');
      }

      setSession({ ...session, ...result });
      setLoadingSession(false);
    },

    /**
     * Signs the user up for a new account
     */
    signUp: async function signUp({ firstName, lastName, email, password, token, ...others }) {
      const requrl = !storeSlug ? '/api/signUp' : `/api/store/${storeSlug}/signUp`;

      const response = await fetch(requrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, password, token, ...others }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'An error occurred during sign up');
      }

      setSession({ ...session, ...result });
    },

    /**
     * Adds items to the cart
     */
    addToCart: async function addToCart({ product, quantity, ...otherParams }) {
      const usertoken = await getUserToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(usertoken && { Authorization: `Bearer ${usertoken}` }),
      };

      const response = await fetch(`/api/store/${storeSlug}/cart/add`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ product, quantity, ...otherParams }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'An unknown error occurred while attempting to add the item to your cart.');
      }

      setSession({ ...session, ...result });
    },

    /**
     * Reorder previous order
     */
    reorder: async function reorder({ slug, order_id }) {
      const usertoken = await getUserToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(usertoken && { Authorization: `Bearer ${usertoken}` }),
      };

      const response = await fetch(`/api/store/${slug}/order/reorder`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ order_id }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'Store is not online at this moment. Please try again later!');
      }

      setSession({ ...session, ...result });
      setLoadingSession(false);

      if (result.hasOwnProperty('error')) {
        setCartError(result.error);
      } else {
        localStorage.setItem('defaulttip', result.tip_type);
        localStorage.setItem('tipamount', result.tip_amount);
      }
    },

    /**
     * Remove all items from the cart
     */
    removeAllFromCart: async function removeAllFromCart() {
      const usertoken = await getUserToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(usertoken && { Authorization: `Bearer ${usertoken}` }),
      };

      const response = await fetch(`/api/store/${storeSlug}/cart/removeAll`, {
        method: 'POST',
        headers,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'An unknown error occurred while attempting to clear your cart.');
      }

      setSession({ ...session, ...result });
    },

    /**
     * Updates items in the cart
     */
    updateCart: async function updateCart({ item, quantity, ...otherParams }) {
      const usertoken = await getUserToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(usertoken && { Authorization: `Bearer ${usertoken}` }),
      };

      const response = await fetch(`/api/store/${storeSlug}/cart/update`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ item, quantity, ...otherParams }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'An unknown error occurred while making changes to your cart.');
      }

      setSession({ ...session, ...result });
    },

    /**
     * Removes a single item from the cart
     */
    removeCartItem: async function removeCartItem({ item, ...otherParams }) {
      const usertoken = await getUserToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(usertoken && { Authorization: `Bearer ${usertoken}` }),
      };

      const response = await fetch(`/api/store/${storeSlug}/cart/remove`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ item, ...otherParams }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || 'An unknown error occurred while removing item from your cart.');
      }

      setSession({ ...session, ...result });
    },
  
    },
  }), [session, loadingSession, config]);

  useEffect(() => {
    const fetchSession = async () => {
      setLoadingSession(true);
      try {
        const token = await getUserToken();
        const headers = {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        };
        const body = storeSlug ? { storeSlug } : {};

        const res = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(body),
          headers,
        });
        const result = await res.json();
        if (result && result.cart) {
          setSession(result);
        }

        try {
          const addr = JSON.parse(localStorage.getItem('userAddress'));
          if (addr?.geometry) setAddress(addr);
        } catch {
          setAddress({});
        }
      } catch (err) {
        console.error('Session fetch failed:', err);
      }
      setLoadingSession(false);
    };

    if (url && user !== null) fetchSession()
  }, [url, user, storeSlug])

  // Listen for Firebase auth state changes to refresh session when email verification status updates
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_MOCK_AUTH === 'true') return;
    if (!config?.tenant_id || !user) return;

    const unsubscribe = getFirebaseApp(config.tenant_id, config.firebase_api_key).auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser && user) {
        const currentEmailVerified = get(session, 'user.email_verified');
        const newEmailVerified = firebaseUser.emailVerified;

        if (currentEmailVerified === false && newEmailVerified === true) {
          try {
            const token = await getUserToken();
            const headers = {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            };
            const body = storeSlug ? { storeSlug } : {};

            const res = await fetch(url, {
              method: 'POST',
              body: JSON.stringify(body),
              headers,
            });
            const result = await res.json();
            setSession(result);
          } catch (err) {
            console.error('Session refresh after email verification failed:', err);
          }
        }
      }
    });

    return () => unsubscribe();
  }, [config?.tenant_id, config?.firebase_api_key, user, session, url, storeSlug, getUserToken])

  return <SessionContext.Provider value={context}>{children}</SessionContext.Provider>;
}

SessionProvider.propTypes = {
  url: PropTypes.string,
};
